import React, { useState, useEffect, Fragment, useRef } from "react";
import { useLoader } from "../../_helper/LoaderContext";
import { getGstDiary } from "./TaxService";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import { Card, CardBody, CardHeader, Col, Row, Table, ButtonGroup, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { ArrowLeftCircle, Download, Printer, Calendar, Filter, X } from "react-feather";
import { useTranslation } from "react-i18next";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useCurrency } from "../../_helper/CurrencyContext";
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FaUpload } from "react-icons/fa6";

const GstDiary = () => {
    const { t } = useTranslation();
    const { getCurrencySymbol } = useCurrency();
    const { setLoading } = useLoader();
    const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
    const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
    const [skeletonLoading, setSkeletonLoading] = useState(true);
    const [getInfo, setInfo] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: '',
    });
    const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
    const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
    const [dateError, setDateError] = useState('');
    const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
    const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);

    // Predefined date ranges
    const dateRanges = [
        { label: t('Today'), days: 0 },
        { label: t('Yesterday'), days: 1 },
        { label: t('Last 7 Days'), days: 7 },
        { label: t('Last 30 Days'), days: 30 },
        { label: t('This Month'), months: 0 },
        { label: t('Last Month'), months: 1 },
        { label: t('Custom Range'), custom: true },
    ];

    useEffect(() => {
        // Set default date range (last 30 days)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        setDateRange({
            startDate: formatDateForInput(startDate),
            endDate: formatDateForInput(endDate),
        });
    }, []);

    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            getGstDiaryDetails();
        }
    }, [dateRange]);

    const formatDateForInput = (date) => {
        return date.toISOString().split('T')[0];
    };

    const getGstDiaryDetails = async () => {
        // Validate dates
        if (!dateRange.startDate || !dateRange.endDate) {
            setDateError(t('Please select both start and end dates'));
            return;
        }

        if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
            setDateError(t('Start date cannot be after end date'));
            return;
        }

        setDateError('');

        const body = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
        }
        try {
            setLoading(true);
            setSkeletonLoading(true);
            const response = await getGstDiary(body)
            if (response.data.status === 'success') {
                setInfo(response.data.data);
                setLoading(false);
                setSkeletonLoading(false);
            }
        } catch (error) {
            setLoading(false);
            setSkeletonLoading(false);
            console.error('Error fetching data:', error);
        }
    }

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Format currency with 2 decimal places
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '0.00';
        return parseFloat(amount).toFixed(2);
    };

    // Get color class based on value (positive/negative)
    const getAmountColor = (amount) => {
        const numAmount = parseFloat(amount);
        if (numAmount > 0) return 'text-danger';
        if (numAmount < 0) return 'text-success';
        return 'text-muted';
    };

    // Handle date range change
    const handleDateChange = (e, field) => {
        setDateRange({
            ...dateRange,
            [field]: e.target.value
        });
    };

    // Apply predefined date range
    const applyDateRange = (range) => {
        const endDate = new Date();
        const startDate = new Date();

        if (range.days !== undefined) {
            startDate.setDate(startDate.getDate() - range.days);
        } else if (range.months !== undefined) {
            if (range.months === 0) { // This month
                startDate.setDate(1);
            } else { // Last month
                startDate.setMonth(startDate.getMonth() - 1);
                startDate.setDate(1);
                endDate.setDate(0); // Last day of previous month
            }
        }

        setDateRange({
            startDate: formatDateForInput(startDate),
            endDate: formatDateForInput(endDate),
        });
    };

    // Export to PDF
    const exportToPDF = () => {
        // You'll need a PDF library like jsPDF or pdfmake
        // This is a placeholder for PDF export functionality
        alert('PDF export functionality would be implemented here');
        // Example with jsPDF:
        // const doc = new jsPDF();
        // doc.text('GST Report', 20, 20);
        // doc.save('gst-report.pdf');
    };

    // Export to Excel
    const exportToExcel = () => {
        if (!getInfo) return;

        const worksheetData = [
            ['GST Report', '', '', ''],
            ['Report Period:', `${formatDate(dateRange.startDate)} to ${formatDate(dateRange.endDate)}`, '', ''],
            ['Generated On:', new Date().toLocaleDateString(), '', ''],
            ['', '', '', ''],
            ['GST Type', 'Input GST (ITC)', 'Output GST Liability', 'Net GST Liability']
            ['CGST', getInfo.paidGST?.inputCGST, getInfo.collectedGST?.outputCGST, getInfo.netGST?.CGST],
            ['SGST', getInfo.paidGST?.inputSGST, getInfo.collectedGST?.outputSGST, getInfo.netGST?.SGST],
            ['IGST', getInfo.paidGST?.inputIGST, getInfo.collectedGST?.outputIGST, getInfo.netGST?.IGST],
            ['TOTAL', getInfo.paidGST?.totalGST, getInfo.collectedGST?.totalGST, getInfo.netGST?.totalGST],
            ['', '', '', ''],
            ['Summary', '', '', ''],
            ['Total Input GST (ITC):', getInfo.paidGST?.totalGST, '', ''],
            ['Total Output GST Liability:', getInfo.collectedGST?.totalGST, '', ''],
            [
                'Net GST Position:',
                getInfo.netGST?.totalGST >= 0
                    ? 'GST Payable to Government'
                    : 'Excess ITC (Carry Forward / Refund Claim)',
                '',
                ''
            ],
            ['Net GST Amount:', getInfo.netGST?.totalGST, '', ''],
        ];

        const ws = XLSX.utils.aoa_to_sheet(worksheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "GST Report");

        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(data, `GST_Report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
    };

    // Export to CSV
    const exportToCSV = () => {
        if (!getInfo) return;

        const csvContent = [
            ['GST Report'],
            [`Report Period:,${formatDate(dateRange.startDate)} to ${formatDate(dateRange.endDate)}`],
            [`Generated On:,${new Date().toLocaleDateString()}`],
            [],
            ['GST Type,Paid GST (Input),Collected GST (Output),Net GST'],
            [`CGST,${getInfo.paidGST?.inputCGST},${getInfo.collectedGST?.outputCGST},${getInfo.netGST?.CGST}`],
            [`SGST,${getInfo.paidGST?.inputSGST},${getInfo.collectedGST?.outputSGST},${getInfo.netGST?.SGST}`],
            [`IGST,${getInfo.paidGST?.inputIGST},${getInfo.collectedGST?.outputIGST},${getInfo.netGST?.IGST}`],
            [`TOTAL,${getInfo.paidGST?.totalGST},${getInfo.collectedGST?.totalGST},${getInfo.netGST?.totalGST}`],
            [],
            ['Summary:'],
            ['Paid GST (Input):,GST paid on purchases and expenses'],
            ['Collected GST (Output):,GST collected from customers on sales'],
            ['Net GST:,Difference between collected and paid GST'],
        ].map(row => row.join('')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `GST_Report_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
    };

    // Print report
    const printReport = () => {
        window.print();
    };

    // Reset date range
    const resetDateRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        setDateRange({
            startDate: formatDateForInput(startDate),
            endDate: formatDateForInput(endDate),
        });
    };

    return (
        <Fragment>
            <Breadcrumbs mainTitle={t('GST Report')} parent={t('Dashboard')} title={t('GST Report')} />
            <div className="container mt-4">
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardHeader className="py-3">
                                <Row className="align-items-center g-3">

                                    {/* Title */}
                                    <Col
                                        xl="3"
                                        lg="4"
                                        md="12"
                                        sm="12"
                                        className="d-flex align-items-center"
                                    >
                                        <h5 className="mb-0">{t('GST Report')}</h5>
                                    </Col>

                                    {/* Filters & Actions */}
                                    <Col
                                        xl="9"
                                        lg="8"
                                        md="12"
                                        sm="12"
                                    >
                                        <Row className="g-2 align-items-center">

                                            {/* Date Range Dropdown */}
                                            <Col
                                                xl="2"
                                                lg="4"
                                                md="6"
                                                sm="12"
                                            >
                                                <Dropdown
                                                    isOpen={dateDropdownOpen}
                                                    toggle={() => setDateDropdownOpen(!dateDropdownOpen)}
                                                    className="w-100"
                                                >
                                                    <DropdownToggle
                                                        caret
                                                        className="btn-sm d-flex align-items-center justify-content-center w-100 text-white"
                                                    >
                                                        <Calendar size={16} className="me-2" />
                                                        {t('Date Range')}
                                                    </DropdownToggle>

                                                    <DropdownMenu>
                                                        {dateRanges.map((range, index) => (
                                                            <DropdownItem
                                                                key={index}
                                                                onClick={() => range.custom ? null : applyDateRange(range)}
                                                            >
                                                                {range.label}
                                                            </DropdownItem>
                                                        ))}
                                                    </DropdownMenu>
                                                </Dropdown>
                                            </Col>

                                            {/* From Date */}
                                            <Col xl="2" lg="3" md="3" sm="6">
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={dateRange.startDate}
                                                    onChange={(e) => handleDateChange(e, 'startDate')}
                                                    max={dateRange.endDate}
                                                />
                                            </Col>

                                            {/* To Date */}
                                            <Col xl="2" lg="3" md="3" sm="6">
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={dateRange.endDate}
                                                    onChange={(e) => handleDateChange(e, 'endDate')}
                                                    min={dateRange.startDate}
                                                />
                                            </Col>

                                            {/* Filter & Clear */}
                                            <Col
                                                xl="3"
                                                lg="6"
                                                md="12"
                                                sm="12"
                                                className="d-flex gap-2"
                                            >
                                                <button
                                                    id="FilterToolTip"
                                                    className="btn btn-primary"
                                                    style={{ height: "35px" }}
                                                    onClick={getGstDiaryDetails}
                                                    disabled={!dateRange.startDate || !dateRange.endDate}
                                                >
                                                    <Filter />
                                                </button>

                                                <ToolTip
                                                    attrToolTip={{
                                                        placement: 'top',
                                                        isOpen: filterTooltipOpen,
                                                        target: 'FilterToolTip',
                                                        toggle: toggleFilterTooltip,
                                                    }}
                                                >
                                                    <strong>{t("Filter")}</strong>
                                                </ToolTip>

                                                <button
                                                    id="ClearToolTip"
                                                    className="btn btn-secondary"
                                                    style={{ height: "35px" }}
                                                    onClick={resetDateRange}
                                                >
                                                    <X />
                                                </button>

                                                <ToolTip
                                                    attrToolTip={{
                                                        placement: 'top',
                                                        isOpen: clearTooltipOpen,
                                                        target: 'ClearToolTip',
                                                        toggle: toggleClearTooltip,
                                                    }}
                                                >
                                                    <strong>{t("Clear")}</strong>
                                                </ToolTip>
                                            </Col>

                                            {/* Export Button */}
                                            <Col
                                                xl="3"
                                                lg="6"
                                                md="12"
                                                sm="12"
                                                className="d-flex justify-content-lg-end"
                                            >
                                                <button
                                                    className="btn btn-primary d-flex align-items-center justify-content-center w-100 w-lg-auto"
                                                    onClick={exportToExcel}
                                                >
                                                    <FaUpload className="me-2" />
                                                    {t("Export to Excel")}
                                                </button>
                                            </Col>

                                        </Row>
                                    </Col>

                                </Row>
                            </CardHeader>
                            {dateError && (
                                <div className="alert alert-danger mx-3 mt-3 mb-0">
                                    {dateError}
                                </div>
                            )}

                            <CardBody>
                                {skeletonLoading ? (
                                    // Skeleton loading state
                                    <div className="row">
                                        <Col md="4">
                                            <Skeleton height={150} />
                                        </Col>
                                        <Col md="4">
                                            <Skeleton height={150} />
                                        </Col>
                                        <Col md="4">
                                            <Skeleton height={150} />
                                        </Col>
                                    </div>
                                ) : getInfo ? (
                                    <>
                                        {/* Date Range Summary */}
                                        <div className="alert alert-primary mb-4">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                                <div>
                                                    <strong>{t('Report Period:')}</strong> {formatDate(dateRange.startDate)} {t('to')} {formatDate(dateRange.endDate)}
                                                </div>
                                                <div>
                                                    <strong>{t('Report Generated:')}</strong> {new Date().toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Rest of your component remains the same... */}
                                        {/* Summary Cards */}
                                        <Row className="mb-4">
                                            <Col md="4">
                                                <Card className="border-success">
                                                    <CardBody>
                                                        <h6 className="card-title text-success">
                                                            <i className="fa fa-download me-2"></i>
                                                            {t('Input GST (ITC Claimable)')}
                                                        </h6>
                                                        <span className="text-muted">
                                                            {t("GST paid on purchases & expenses")}
                                                        </span>
                                                        <div className="mt-3">
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('CGST')}:</span>
                                                                <strong>{getCurrencySymbol()}{formatCurrency(getInfo.paidGST?.inputCGST)}</strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('SGST')}:</span>
                                                                <strong>{getCurrencySymbol()}{formatCurrency(getInfo.paidGST?.inputSGST)}</strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('IGST')}:</span>
                                                                <strong>{getCurrencySymbol()}{formatCurrency(getInfo.paidGST?.inputIGST)}</strong>
                                                            </div>
                                                            <hr />
                                                            <div className="d-flex justify-content-between">
                                                                <span className="fw-bold">{t('Total Input GST:')}</span>
                                                                <strong className="fs-5">{getCurrencySymbol()}{formatCurrency(getInfo.paidGST?.totalGST)}</strong>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>

                                            <Col md="4">
                                                <Card className="border-primary">
                                                    <CardBody>
                                                        <h6 className="card-title text-primary">
                                                            <i className="fa fa-upload me-2"></i>
                                                            {t('Output GST Liability')}
                                                        </h6>
                                                        <span>{t("GST collected from customers")}</span>
                                                        <div className="mt-3">
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('CGST')}:</span>
                                                                <strong>{getCurrencySymbol()}{formatCurrency(getInfo.collectedGST?.outputCGST)}</strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('SGST')}:</span>
                                                                <strong>{getCurrencySymbol()}{formatCurrency(getInfo.collectedGST?.outputSGST)}</strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('IGST')}:</span>
                                                                <strong>{getCurrencySymbol()}{formatCurrency(getInfo.collectedGST?.outputIGST)}</strong>
                                                            </div>
                                                            <hr />
                                                            <div className="d-flex justify-content-between">
                                                                <span className="fw-bold">{t('Total Output GST:')}</span>
                                                                <strong className="fs-5">{getCurrencySymbol()}{formatCurrency(getInfo.collectedGST?.totalGST)}</strong>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>

                                            <Col md="4">
                                                <Card className={`border-${getInfo.netGST?.totalGST >= 0 ? 'warning' : 'danger'}`}>
                                                    <CardBody>
                                                        <h6 className={`card-title text-${getInfo.netGST?.totalGST >= 0 ? 'warning' : 'danger'}`}>
                                                            <i className="fa fa-balance-scale me-2"></i>
                                                            {t('Net GST Liability')}
                                                        </h6>
                                                        <div className="mt-3">
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('CGST')}:</span>
                                                                <strong className={getAmountColor(getInfo.netGST?.CGST)}>
                                                                    {getCurrencySymbol()}{formatCurrency(getInfo.netGST?.CGST)}
                                                                </strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('SGST')}:</span>
                                                                <strong className={getAmountColor(getInfo.netGST?.SGST)}>
                                                                    {getCurrencySymbol()}{formatCurrency(getInfo.netGST?.SGST)}
                                                                </strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('IGST')}:</span>
                                                                <strong className={getAmountColor(getInfo.netGST?.IGST)}>
                                                                    {getCurrencySymbol()}{formatCurrency(getInfo.netGST?.IGST)}
                                                                </strong>
                                                            </div>
                                                            <hr />
                                                            <div className="d-flex justify-content-between">
                                                                <span className="fw-bold">{t('Net GST Liability:')}</span>
                                                                <strong className={`fs-5 ${getAmountColor(getInfo.netGST?.totalGST)}`}>
                                                                    {getCurrencySymbol()}{formatCurrency(getInfo.netGST?.totalGST)}
                                                                </strong>
                                                            </div>
                                                            <div className={`mt-2 text-center ${getInfo.netGST?.totalGST >= 0 ? 'text-warning' : 'text-success'}`}>
                                                                <small>
                                                                    {getInfo.netGST?.totalGST >= 0
                                                                        ? t('Amount Payable to Tax Authority')
                                                                        : t('Refundable Amount from Tax Authority')}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>

                                        {/* Detailed Table View */}
                                        <Row>
                                            <Col xs="12">
                                                <Card>
                                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                                        <h6 className="mb-0">{t('Detailed GST Analysis')}</h6>
                                                        <small className="text-muted">
                                                            {t('All amounts in')} {getCurrencySymbol()}
                                                        </small>
                                                    </CardHeader>
                                                    <CardBody>
                                                        {/* Your table remains the same */}
                                                        <Table responsive bordered>
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>{t('GST Type')}</th>
                                                                    <th className="text-center">{t('Input GST (Paid)')}</th>
                                                                    <th className="text-center">{t('Output GST (Collected)')}</th>
                                                                    <th className="text-center">{t('Net GST')}</th>
                                                                    <th className="text-center">{t('Settlement Status')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td><strong>{t('Central GST (CGST)')}</strong></td>
                                                                    <td className="text-center">{getCurrencySymbol()}{formatCurrency(getInfo.paidGST?.inputCGST)}</td>
                                                                    <td className="text-center">{getCurrencySymbol()}{formatCurrency(getInfo.collectedGST?.outputCGST)}</td>
                                                                    <td className={`text-center ${getAmountColor(getInfo.netGST?.CGST)}`}>
                                                                        {getCurrencySymbol()}{formatCurrency(getInfo.netGST?.CGST)}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {getInfo.netGST?.CGST >= 0 ? (
                                                                            <span className="badge bg-warning">{t('Payable')}</span>
                                                                        ) : (
                                                                            <span className="badge bg-success">{t('Refundable')}</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>{t('State GST (SGST)')}</strong></td>
                                                                    <td className="text-center">{getCurrencySymbol()}{formatCurrency(getInfo.paidGST?.inputSGST)}</td>
                                                                    <td className="text-center">{getCurrencySymbol()}{formatCurrency(getInfo.collectedGST?.outputSGST)}</td>
                                                                    <td className={`text-center ${getAmountColor(getInfo.netGST?.SGST)}`}>
                                                                        {getCurrencySymbol()}{formatCurrency(getInfo.netGST?.SGST)}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {getInfo.netGST?.SGST >= 0 ? (
                                                                            <span className="badge bg-warning">{t('Payable')}</span>
                                                                        ) : (
                                                                            <span className="badge bg-success">{t('Refundable')}</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>{t('Integrated GST (IGST)')}</strong></td>
                                                                    <td className="text-center">{getCurrencySymbol()}{formatCurrency(getInfo.paidGST?.inputIGST)}</td>
                                                                    <td className="text-center">{getCurrencySymbol()}{formatCurrency(getInfo.collectedGST?.outputIGST)}</td>
                                                                    <td className={`text-center ${getAmountColor(getInfo.netGST?.IGST)}`}>
                                                                        {getCurrencySymbol()}{formatCurrency(getInfo.netGST?.IGST)}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {getInfo.netGST?.IGST >= 0 ? (
                                                                            <span className="badge bg-warning">{t('Payable')}</span>
                                                                        ) : (
                                                                            <span className="badge bg-success">{t('Refundable')}</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                                <tr className="bg-light">
                                                                    <td><strong>{t('TOTAL')}</strong></td>
                                                                    <td className="text-center">
                                                                        <strong>{getCurrencySymbol()}{formatCurrency(getInfo.paidGST?.totalGST)}</strong>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <strong>{getCurrencySymbol()}{formatCurrency(getInfo.collectedGST?.totalGST)}</strong>
                                                                    </td>
                                                                    <td className={`text-center ${getAmountColor(getInfo.netGST?.totalGST)}`}>
                                                                        <strong>{getCurrencySymbol()}{formatCurrency(getInfo.netGST?.totalGST)}</strong>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {getInfo.netGST?.totalGST >= 0 ? (
                                                                            <span className="badge bg-warning">{t('Net Payable')}</span>
                                                                        ) : (
                                                                            <span className="badge bg-success">{t('Net Refundable')}</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </Table>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>

                                        {/* Summary Notes */}
                                        <Row className="mt-4">
                                            <Col xs="12">
                                                <div className="alert alert-light">
                                                    <h6>{t('Report Summary')}</h6>
                                                    <ul className="mb-0">
                                                        <li>
                                                            <strong>{t('Input GST:')}</strong> {t('Goods and Services Tax paid on business purchases and expenses during the selected period.')}
                                                        </li>
                                                        <li>
                                                            <strong>{t('Output GST:')}</strong> {t('Goods and Services Tax collected from customers on sales and services rendered.')}
                                                        </li>
                                                        <li>
                                                            <strong>{t('Net GST Liability:')}</strong> {t('The difference between output GST and input GST. A positive amount indicates tax payable to the government, while a negative amount indicates a refund claim.')}
                                                        </li>
                                                        <li>
                                                            <strong>{t('CGST & SGST:')}</strong> {t('Applicable for intra-state transactions.')}
                                                        </li>
                                                        <li>
                                                            <strong>{t('IGST:')}</strong> {t('Applicable for inter-state transactions.')}
                                                        </li>
                                                    </ul>
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="text-muted mb-3">
                                            <i className="fa fa-file-alt fa-3x"></i>
                                        </div>
                                        <h5>{t('No GST Data Available')}</h5>
                                        <p className="text-muted">
                                            {t('Please select a date range and click "Apply" to generate GST report.')}
                                        </p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>
    )
}

export default GstDiary;